import React, { useState, useEffect } from 'react';
import {
  FileText, Shield, Scale, AlertTriangle, Building2, Lock,
  ChevronDown, ChevronUp, ExternalLink, BookOpen, Loader2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/new-components/ui/card';
import { Badge } from '../components/new-components/ui/badge';
import api from '../services/api';
import { TENANT } from '../config';
import MarkdownRenderer from '../components/MarkdownRenderer';

interface LegalDocument {
  id: string;
  document_type: string;
  version: number;
  title: string;
  content_snapshot: string;
  effective_date: string;
  created_at: string;
}

interface ComplianceDoc {
  id: string;
  title: string;
  description: string;
  category: string;
  effectiveDate: string;
  content?: string;
}

const LEGAL_DOC_ICONS: Record<string, React.ReactNode> = {
  terms: <Scale className="h-5 w-5 text-blue-600" />,
  risk_disclosure: <AlertTriangle className="h-5 w-5 text-amber-600" />,
  ach_authorization: <Building2 className="h-5 w-5 text-green-600" />,
  crypto_irrevocable: <Lock className="h-5 w-5 text-purple-600" />,
  privacy_policy: <Shield className="h-5 w-5 text-indigo-600" />,
  subscription_agreement: <FileText className="h-5 w-5 text-teal-600" />,
};

const COMPLIANCE_DOC_ICONS: Record<string, React.ReactNode> = {
  'card-data-scope': <Shield className="h-5 w-5 text-green-600" />,
  'ach-crypto-risk': <Building2 className="h-5 w-5 text-blue-600" />,
  'logging-retention': <FileText className="h-5 w-5 text-orange-600" />,
};

const CATEGORY_COLORS: Record<string, string> = {
  'PCI Compliance': 'bg-green-100 text-green-800',
  'Risk & Compliance': 'bg-blue-100 text-blue-800',
  'Operations': 'bg-orange-100 text-orange-800',
};

// Unwrap API response: handles both axios-wrapped and direct body
const unwrap = (res: any) => {
  const body = res?.data?.success !== undefined ? res.data : (res?.success !== undefined ? res : res?.data || res);
  return body;
};

const Documentation: React.FC = () => {
  const [legalDocs, setLegalDocs] = useState<LegalDocument[]>([]);
  const [complianceDocs, setComplianceDocs] = useState<ComplianceDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedDoc, setExpandedDoc] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'legal' | 'compliance'>('legal');

  const brandName = TENANT?.branding?.name || 'Platform';

  useEffect(() => {
    const fetchDocs = async () => {
      setLoading(true);
      try {
        const [legalRes, complianceRes] = await Promise.all([
          api.get('/legal/documents'),
          api.get('/legal/compliance-docs'),
        ]);
        setLegalDocs(unwrap(legalRes)?.data || []);
        setComplianceDocs(unwrap(complianceRes)?.data || []);
      } catch (err) {
        console.error('Failed to load documentation:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchDocs();
  }, []);

  const toggleExpand = (id: string) => {
    setExpandedDoc(prev => (prev === id ? null : id));
  };

  const fmtDate = (d: string) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-US', {
      month: 'long', day: 'numeric', year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <BookOpen className="h-7 w-7 text-primary" />
          <h1 className="text-2xl font-bold text-foreground">Documentation</h1>
        </div>
        <p className="text-muted-foreground">
          Legal agreements, compliance policies, and platform documentation for {brandName}.
        </p>
      </div>

      {/* Tab Switcher */}
      <div className="flex gap-1 mb-6 bg-muted p-1 rounded-lg w-fit">
        <button
          onClick={() => setActiveTab('legal')}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
            activeTab === 'legal'
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          <Scale className="h-4 w-4 inline-block mr-1.5 -mt-0.5" />
          Legal Agreements ({legalDocs.length})
        </button>
        <button
          onClick={() => setActiveTab('compliance')}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
            activeTab === 'compliance'
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          <Shield className="h-4 w-4 inline-block mr-1.5 -mt-0.5" />
          Compliance Policies ({complianceDocs.length})
        </button>
      </div>

      {/* Legal Agreements Tab */}
      {activeTab === 'legal' && (
        <div className="space-y-3">
          {legalDocs.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <FileText className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">No legal documents available.</p>
              </CardContent>
            </Card>
          ) : (
            legalDocs.map(doc => (
              <Card key={doc.id} className="transition-shadow hover:shadow-md">
                <button
                  onClick={() => toggleExpand(doc.id)}
                  className="w-full text-left"
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        {LEGAL_DOC_ICONS[doc.document_type] || <FileText className="h-5 w-5 text-muted-foreground" />}
                        <div>
                          <CardTitle className="text-base">{doc.title}</CardTitle>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className="text-xs">
                              v{doc.version}
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              Effective {fmtDate(doc.effective_date || doc.created_at)}
                            </span>
                          </div>
                        </div>
                      </div>
                      {expandedDoc === doc.id ? (
                        <ChevronUp className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-1" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-1" />
                      )}
                    </div>
                  </CardHeader>
                </button>
                {expandedDoc === doc.id && (
                  <CardContent className="pt-0 pb-4">
                    <div className="border-t border-border pt-4 mt-1">
                      <div className="bg-muted/50 rounded-lg p-4 max-h-[600px] overflow-y-auto">
                        <MarkdownRenderer content={doc.content_snapshot || 'Document content not available.'} />
                      </div>
                    </div>
                  </CardContent>
                )}
              </Card>
            ))
          )}
        </div>
      )}

      {/* Compliance Policies Tab */}
      {activeTab === 'compliance' && (
        <div className="space-y-3">
          {complianceDocs.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Shield className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">No compliance documents available.</p>
              </CardContent>
            </Card>
          ) : (
            complianceDocs.map(doc => (
              <Card key={doc.id} className="transition-shadow hover:shadow-md">
                <button
                  onClick={() => toggleExpand(`compliance-${doc.id}`)}
                  className="w-full text-left"
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-start gap-3">
                      {COMPLIANCE_DOC_ICONS[doc.id] || <FileText className="h-5 w-5 text-muted-foreground" />}
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <CardTitle className="text-base">{doc.title}</CardTitle>
                          <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                            <Badge className={`text-xs ${CATEGORY_COLORS[doc.category] || 'bg-gray-100 text-gray-800'}`}>
                              {doc.category}
                            </Badge>
                            {expandedDoc === `compliance-${doc.id}` ? (
                              <ChevronUp className="h-4 w-4 text-muted-foreground" />
                            ) : (
                              <ChevronDown className="h-4 w-4 text-muted-foreground" />
                            )}
                          </div>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">{doc.description}</p>
                        <p className="text-xs text-muted-foreground mt-2">
                          Effective {fmtDate(doc.effectiveDate)}
                        </p>
                      </div>
                    </div>
                  </CardHeader>
                </button>
                {expandedDoc === `compliance-${doc.id}` && doc.content && (
                  <CardContent className="pt-0 pb-4">
                    <div className="border-t border-border pt-4 mt-1">
                      <div className="bg-muted/50 rounded-lg p-4 max-h-[600px] overflow-y-auto">
                        <MarkdownRenderer content={doc.content} />
                      </div>
                    </div>
                  </CardContent>
                )}
              </Card>
            ))
          )}

          <div className="mt-6 text-xs text-muted-foreground px-1">
            <p>
              Full compliance documents are maintained by Platform Engineering and are available
              upon request for institutional partners, banking counterparties, and compliance reviewers.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Documentation;
